import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useCart } from '@/App';
import { ArrowLeft, MapPin, Truck, Store, CreditCard, Banknote, QrCode, MessageCircle, Check, Star, Gift, Search, Navigation, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/AddressAutocomplete';

const Checkout = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { cart, getTotal, clearCart } = useCart();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  
  // Last address feature
  const [lastAddress, setLastAddress] = useState(null);
  const [lastAddressLoading, setLastAddressLoading] = useState(false);
  
  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    delivery_type: 'delivery',
    address: {
      formatted: '',
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      reference: '',
      latitude: null,
      longitude: null
    },
    payment_method: 'pix',
    change_for: '',
    notes: '',
    coupon_code: '',
    lgpd_consent: false
  });
  
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  
  const [lgpdError, setLgpdError] = useState(false);
  
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  
  const [deliveryFeeData, setDeliveryFeeData] = useState(null);
  const [calculatingDelivery, setCalculatingDelivery] = useState(false);

  useEffect(() => {
    if (cart.items.length === 0) {
      navigate(`/${slug}`);
      return;
    }
    fetchStore();
  }, [slug]);

  const fetchStore = async () => {
    try {
      const res = await axios.get(`${API}/public/stores/${slug}`);
      setStore(res.data);
    } catch (e) {
      toast.error('Loja não encontrada');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Buscar último endereço do cliente pelo telefone
  const fetchLastAddress = async (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setLastAddress(null);
      return;
    }
    
    setLastAddressLoading(true);
    try {
      const res = await axios.get(`${API}/public/stores/${slug}/customer-last-address/${cleanPhone}`);
      if (res.data.found) {
        setLastAddress(res.data);
      } else {
        setLastAddress(null);
      }
    } catch (e) {
      console.error('Error fetching last address:', e);
      setLastAddress(null);
    } finally {
      setLastAddressLoading(false);
    }
  };

  // Usar último endereço
  const useLastAddress = () => {
    if (!lastAddress) return;
    
    const addr = lastAddress.address;
    const newAddress = {
      formatted: `${addr.street}, ${addr.number} - ${addr.neighborhood}, ${addr.city}`,
      cep: addr.cep || '',
      street: addr.street || '',
      number: addr.number || '',
      complement: addr.complement || '',
      neighborhood: addr.neighborhood || '',
      city: addr.city || '',
      state: addr.state || '',
      reference: addr.reference || '',
      latitude: addr.latitude || null,
      longitude: addr.longitude || null
    };
    
    setForm(prev => ({
      ...prev,
      customer_name: prev.customer_name || lastAddress.customer_name || '',
      address: newAddress
    }));
    
    toast.success('Endereço do último pedido aplicado!');
    
    // Calculate delivery fee with the new address
    if (newAddress.latitude && newAddress.longitude) {
      calculateDeliveryFeeWithCoordinates(newAddress.latitude, newAddress.longitude);
    } else if (newAddress.street && newAddress.neighborhood) {
      calculateDeliveryFeeWithAddress(newAddress);
    }
  };

  // Formatar CEP com máscara (00000-000)
  const formatCep = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    return digits;
  };

  // Buscar endereço por CEP (com fallback para BrasilAPI)
  const fetchAddressByCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      toast.error('Digite um CEP válido com 8 dígitos');
      return;
    }
    
    setCepLoading(true);
    
    // Helper function to process CEP data
    const processAddressData = (data, source) => {
      const newAddress = {
        ...form.address,
        cep: cleanCep,
        street: source === 'viacep' ? (data.logradouro || '') : (data.street || ''),
        neighborhood: source === 'viacep' ? (data.bairro || '') : (data.neighborhood || ''),
        city: source === 'viacep' ? (data.localidade || '') : (data.city || ''),
        state: source === 'viacep' ? (data.uf || '') : (data.state || ''),
      };
      newAddress.formatted = `${newAddress.street}, ${newAddress.neighborhood}, ${newAddress.city} - ${newAddress.state}`;
      return newAddress;
    };
    
    try {
      // Try ViaCEP first
      let data = null;
      let source = 'viacep';
      
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`, { 
          signal: AbortSignal.timeout(5000) 
        });
        if (res.ok) {
          const text = await res.text();
          data = JSON.parse(text);
          if (data.erro) data = null;
        }
      } catch (e) {
        console.log('ViaCEP failed, trying BrasilAPI...');
      }
      
      // Fallback to BrasilAPI if ViaCEP failed
      if (!data) {
        try {
          const res = await fetch(`https://brasilapi.com.br/api/cep/v1/${cleanCep}`, {
            signal: AbortSignal.timeout(5000)
          });
          if (res.ok) {
            data = await res.json();
            source = 'brasilapi';
          }
        } catch (e) {
          console.log('BrasilAPI also failed');
        }
      }
      
      if (!data) {
        toast.error('CEP não encontrado. Verifique se o CEP está correto ou digite o endereço manualmente.');
        return;
      }
      
      const newAddress = processAddressData(data, source);
      
      setForm(prev => ({
        ...prev,
        address: newAddress
      }));
      
      toast.success('Endereço encontrado!');
      
      // Trigger delivery fee calculation
      if (newAddress.street && newAddress.neighborhood) {
        calculateDeliveryFeeWithAddress(newAddress);
      }
    } catch (e) {
      console.error('CEP error:', e);
      toast.error('Erro ao buscar CEP. Tente digitar o endereço manualmente.');
    } finally {
      setCepLoading(false);
    }
  };

  // Handle Google Maps address selection
  const handleAddressSelect = (addressData) => {
    const newAddress = {
      formatted: addressData.formatted || '',
      cep: addressData.cep || form.address.cep,
      street: addressData.street || '',
      number: addressData.number || '',
      complement: form.address.complement,
      neighborhood: addressData.neighborhood || '',
      city: addressData.city || '',
      state: addressData.state || '',
      reference: form.address.reference,
      latitude: addressData.latitude || null,
      longitude: addressData.longitude || null
    };
    
    setForm(prev => ({
      ...prev,
      address: newAddress
    }));
    
    // Calculate delivery fee immediately with coordinates
    if (newAddress.latitude && newAddress.longitude) {
      calculateDeliveryFeeWithCoordinates(newAddress.latitude, newAddress.longitude);
    } else if (newAddress.street && newAddress.neighborhood) {
      calculateDeliveryFeeWithAddress(newAddress);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setForm(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };

  // Calculate delivery fee with coordinates (most accurate)
  const calculateDeliveryFeeWithCoordinates = async (lat, lng) => {
    setCalculatingDelivery(true);
    try {
      const res = await axios.post(`${API}/public/stores/${slug}/calculate-delivery`, {
        latitude: lat,
        longitude: lng
      });
      setDeliveryFeeData(res.data);
    } catch (e) {
      console.error('Error calculating delivery fee:', e);
      setDeliveryFeeData(null);
    } finally {
      setCalculatingDelivery(false);
    }
  };

  // Calculate delivery fee with address (fallback)
  const calculateDeliveryFeeWithAddress = async (addressData) => {
    setCalculatingDelivery(true);
    try {
      const res = await axios.post(`${API}/public/stores/${slug}/calculate-delivery`, {
        street: addressData.street,
        number: addressData.number,
        neighborhood: addressData.neighborhood,
        city: addressData.city || store?.address?.city || '',
        state: addressData.state || store?.address?.state || 'SP'
      });
      setDeliveryFeeData(res.data);
    } catch (e) {
      console.error('Error calculating delivery fee:', e);
      setDeliveryFeeData(null);
    } finally {
      setCalculatingDelivery(false);
    }
  };

  const calculateDeliveryFee = async () => {
    if (form.delivery_type !== 'delivery') return;
    
    // Prefer coordinates if available
    if (form.address.latitude && form.address.longitude) {
      calculateDeliveryFeeWithCoordinates(form.address.latitude, form.address.longitude);
      return;
    }
    
    if (!form.address.street || !form.address.neighborhood) return;
    calculateDeliveryFeeWithAddress(form.address);
  };

  // Calculate delivery fee when address changes
  useEffect(() => {
    const timer = setTimeout(() => {
      // Always calculate delivery fee when we have address data
      if (form.delivery_type === 'delivery' && (form.address.street || form.address.latitude)) {
        calculateDeliveryFee();
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [form.address.latitude, form.address.longitude, form.address.street, form.address.number, form.address.neighborhood, form.address.city, form.delivery_type]);

  const validateCoupon = async () => {
    if (!form.coupon_code) return;
    setCouponLoading(true);
    try {
      const res = await axios.get(
        `${API}/public/stores/${slug}/coupons/validate/${form.coupon_code}?subtotal=${getTotal()}`
      );
      setCoupon(res.data);
      toast.success('Cupom aplicado!');
    } catch (e) {
      setCoupon(null);
      toast.error(e.response?.data?.detail || 'Cupom inválido');
    } finally {
      setCouponLoading(false);
    }
  };

  const fetchLoyaltyPoints = async () => {
    if (!form.customer_phone || form.customer_phone.length < 10) return;
    setLoyaltyLoading(true);
    try {
      const cleanPhone = form.customer_phone.replace(/\D/g, '');
      const res = await axios.get(`${API}/public/stores/${slug}/loyalty/${cleanPhone}`);
      setLoyalty(res.data);
      setPointsToRedeem(0); // Reset when phone changes
    } catch (e) {
      setLoyalty(null);
    } finally {
      setLoyaltyLoading(false);
    }
  };

  // Fetch loyalty points when phone changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.customer_phone.replace(/\D/g, '').length >= 10) {
        fetchLoyaltyPoints();
        fetchLastAddress(form.customer_phone);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.customer_phone]);

  const getDeliveryFee = () => {
    if (form.delivery_type === 'pickup' || !store?.delivery_fee) return 0;
    
    // Use calculated delivery fee if available (for per_km type)
    if (store.delivery_fee.type === 'per_km' && deliveryFeeData) {
      return deliveryFeeData.fee || 0;
    }
    
    return store.delivery_fee.fixed_value || 0;
  };

  const isDeliveryAvailable = () => {
    if (form.delivery_type === 'pickup') return true;
    
    // If we have delivery fee data, check if available
    if (deliveryFeeData) {
      return deliveryFeeData.available !== false;
    }
    
    // If no data yet, allow (will be calculated)
    return true;
  };

  const getDiscount = () => {
    if (!coupon) return 0;
    const subtotal = getTotal();
    if (coupon.type === 'percentage') {
      return subtotal * (coupon.value / 100);
    } else if (coupon.type === 'fixed') {
      return coupon.value;
    } else if (coupon.type === 'free_delivery') {
      return getDeliveryFee();
    }
    return 0;
  };

  const getLoyaltyDiscount = () => {
    if (!loyalty?.enabled || pointsToRedeem <= 0) return 0;
    return pointsToRedeem * (loyalty.redemption_value || 0.01);
  };

  const getFinalTotal = () => {
    return Math.max(0, getTotal() + getDeliveryFee() - getDiscount() - getLoyaltyDiscount());
  };

  const generateWhatsAppMessage = (order) => {
    const subtotal = getTotal();
    const deliveryFee = getDeliveryFee();
    const discount = getDiscount();
    const loyaltyDiscount = getLoyaltyDiscount();
    const total = getFinalTotal();
    
    // Use order number from saved order
    const orderNumber = order?.order_number || Date.now().toString().slice(-6);
    
    let msg = `🛒 *NOVO PEDIDO #${orderNumber}*\n\n`;
    msg += `👤 *Cliente:* ${form.customer_name}\n`;
    msg += `📱 *Telefone:* ${form.customer_phone}\n\n`;
    
    if (form.delivery_type === 'delivery') {
      msg += `📍 *Endereço:*\n`;
      msg += `${form.address.street}, ${form.address.number}\n`;
      if (form.address.complement) msg += `${form.address.complement}\n`;
      msg += `${form.address.neighborhood}`;
      if (form.address.city) msg += ` - ${form.address.city}`;
      msg += `\n`;
      if (form.address.reference) msg += `Ref: ${form.address.reference}\n`;
      msg += `\n`;
    } else {
      msg += `🏪 *Retirada no local*\n\n`;
    }
    
    msg += `📦 *Itens:*\n`;
    cart.items.forEach(item => {
      msg += `• ${item.quantity}x ${item.product_name}`;
      if (item.variant_name) msg += ` (${item.variant_name})`;
      msg += ` - R$ ${(item.unit_price * item.quantity).toFixed(2)}\n`;
      if (item.addons?.length > 0) {
        item.addons.forEach(addon => {
          msg += `  + ${addon.quantity}x ${addon.name}\n`;
        });
      }
      if (item.notes) msg += `  📝 ${item.notes}\n`;
    });
    
    msg += `\n💰 *Subtotal:* R$ ${subtotal.toFixed(2)}\n`;
    if (deliveryFee > 0) msg += `🚚 *Taxa de entrega:* R$ ${deliveryFee.toFixed(2)}\n`;
    if (discount > 0) msg += `🎫 *Desconto:* -R$ ${discount.toFixed(2)}\n`;
    if (loyaltyDiscount > 0) msg += `⭐ *Pontos resgatados:* -R$ ${loyaltyDiscount.toFixed(2)}\n`;
    msg += `*TOTAL: R$ ${total.toFixed(2)}*\n\n`;
    
    const paymentLabels = { pix: 'Pix', cash: 'Dinheiro', card: 'Cartão' };
    msg += `💳 *Pagamento:* ${paymentLabels[form.payment_method]}\n`;
    if (form.payment_method === 'cash' && form.change_for) {
      msg += `💵 *Troco para:* R$ ${form.change_for}\n`;
    }
    
    if (form.notes) msg += `\n📝 *Observações:* ${form.notes}\n`;
    
    return encodeURIComponent(msg);
  };

  const handleSubmit = async () => {
    if (!form.customer_name || !form.customer_phone) {
      toast.error('Preencha seu nome e telefone');
      return;
    }
    
    if (form.delivery_type === 'delivery' && (!form.address.street || !form.address.number || !form.address.neighborhood)) {
      toast.error('Preencha o endereço completo');
      return;
    }
    
    if (form.delivery_type === 'delivery' && !isDeliveryAvailable()) {
      toast.error(deliveryFeeData?.message || 'Entrega não disponível para este endereço');
      return;
    }
    
    if (!form.lgpd_consent) {
      toast.error('Aceite os termos de privacidade para continuar');
      setLgpdError(true);
      // Scroll to the LGPD checkbox
      document.getElementById('lgpd-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!store?.whatsapp) {
      toast.error('Loja não configurou o WhatsApp para receber pedidos');
      return;
    }

    setSubmitting(true);
    
    try {
      // Build order data
      const orderData = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone.replace(/\D/g, ''),
        delivery_type: form.delivery_type,
        address: form.delivery_type === 'delivery' ? form.address : null,
        payment_method: form.payment_method,
        change_for: form.payment_method === 'cash' && form.change_for ? parseFloat(form.change_for) : null,
        items: cart.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          variant_id: item.variant_id,
          variant_name: item.variant_name,
          addons: item.addons || [],
          notes: item.notes || ''
        })),
        coupon_code: form.coupon_code || null,
        loyalty_points_redeem: pointsToRedeem,
        notes: form.notes,
        lgpd_consent: form.lgpd_consent
      };

      // Save order to database
      const res = await axios.post(`${API}/public/stores/${slug}/orders`, orderData);
      const order = res.data;
      
      // Generate WhatsApp message with order number
      const whatsappNumber = store.whatsapp.replace(/\D/g, '');
      const message = generateWhatsAppMessage(order);
      const whatsappUrl = `https://wa.me/55${whatsappNumber}?text=${message}`;
      
      // Try to open WhatsApp
      const whatsappWindow = window.open(whatsappUrl, '_blank');
      
      // Check if pop-up was blocked
      if (!whatsappWindow || whatsappWindow.closed || typeof whatsappWindow.closed === 'undefined') {
        // Pop-up was blocked - show toast with link
        toast.info(
          <div>
            <p className="font-bold">Pop-up bloqueado!</p>
            <p className="text-sm mt-1">Clique aqui para enviar pelo WhatsApp:</p>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-green-400 underline text-sm mt-2 block"
              onClick={(e) => e.stopPropagation()}
            >
              Abrir WhatsApp
            </a>
          </div>,
          { duration: 10000 }
        );
      }
      
      // Clear cart and show success
      clearCart();
      toast.success('Pedido realizado com sucesso!');
      
      // Navigate to order tracking page
      navigate(`/${slug}/pedido/${order.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao realizar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a2e] pb-32">
      <header className="sticky top-0 z-50 bg-[#0a0a0f] border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => navigate(`/${slug}/carrinho`)}
              className="text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-bold text-white">Finalizar Pedido</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Customer Info */}
        <div className="card-adegga p-4 space-y-4">
          <h2 className="font-bold text-white">Seus Dados</h2>
          <Input
            placeholder="Seu nome"
            value={form.customer_name}
            onChange={(e) => handleInputChange('customer_name', e.target.value)}
            className="bg-[#0f0f15] border-white/10 text-white"
            data-testid="customer-name"
          />
          <Input
            placeholder="WhatsApp (11) 99999-9999"
            value={form.customer_phone}
            onChange={(e) => handleInputChange('customer_phone', e.target.value)}
            className="bg-[#0f0f15] border-white/10 text-white"
            data-testid="customer-phone"
          />
          
          {/* LGPD Consent - Logo abaixo dos dados */}
          <div 
            id="lgpd-section"
            className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
              lgpdError && !form.lgpd_consent 
                ? 'bg-red-500/20 border-2 border-red-500 animate-pulse' 
                : form.lgpd_consent 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-[#0f0f15] border border-white/10'
            }`}
          >
            <Checkbox
              id="lgpd"
              checked={form.lgpd_consent}
              onCheckedChange={(checked) => {
                handleInputChange('lgpd_consent', checked);
                if (checked) setLgpdError(false);
              }}
              className={lgpdError && !form.lgpd_consent ? 'border-red-500' : ''}
            />
            <div className="flex-1">
              <Label 
                htmlFor="lgpd" 
                className={`text-sm cursor-pointer ${
                  lgpdError && !form.lgpd_consent 
                    ? 'text-red-400 font-medium' 
                    : form.lgpd_consent 
                      ? 'text-green-400' 
                      : 'text-white/60'
                }`}
              >
                Concordo com o tratamento dos meus dados pessoais para processamento do pedido (LGPD)
              </Label>
              {lgpdError && !form.lgpd_consent && (
                <p className="text-red-400 text-xs mt-1 font-medium">
                  ⚠️ Você precisa aceitar para continuar!
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Address - ALWAYS VISIBLE, BEFORE Delivery Type */}
        <div className="card-adegga p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#ff6b00]" />
              Seu Endereço
            </h2>
            
            {/* Last address button */}
            {lastAddressLoading ? (
              <div className="flex items-center gap-2 text-sm text-white/50">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Buscando...</span>
              </div>
            ) : lastAddress ? (
              <Button
                type="button"
                onClick={useLastAddress}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white text-xs h-8"
              >
                <Check className="w-3 h-3 mr-1" />
                Usar último endereço
              </Button>
            ) : null}
          </div>
          
          {/* Show last address preview */}
          {lastAddress && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
              <p className="text-xs text-green-400 mb-1">Endereço do último pedido:</p>
              <p className="text-sm text-white">
                {lastAddress.address.street}, {lastAddress.address.number}
                {lastAddress.address.complement && ` - ${lastAddress.address.complement}`}
              </p>
              <p className="text-xs text-white/60">
                {lastAddress.address.neighborhood}, {lastAddress.address.city}
              </p>
            </div>
          )}
          
          {/* Google Maps Autocomplete */}
          <AddressAutocomplete
            value={form.address.formatted}
            onChange={handleAddressSelect}
            placeholder="Pesquisar endereço no Google Maps..."
          />
          
          {/* Ou buscar por CEP */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#13131f] px-2 text-white/40">ou digite o CEP</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Input
              placeholder="CEP (ex: 01310-100)"
              value={formatCep(form.address.cep || '')}
              onChange={(e) => handleInputChange('address.cep', e.target.value.replace(/\D/g, ''))}
              className="flex-1 bg-[#0f0f15] border-white/10 text-white"
              maxLength={9}
            />
            <Button
              type="button"
              onClick={() => fetchAddressByCep(form.address.cep)}
              disabled={cepLoading || !form.address.cep || form.address.cep.replace(/\D/g, '').length < 8}
              className="btn-secondary"
            >
              {cepLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><Search className="w-4 h-4 mr-1" /> Buscar</>
              )}
            </Button>
          </div>
          
          {/* Manual address fields */}
          <div className="grid grid-cols-3 gap-3">
            <Input
              placeholder="Rua"
              value={form.address.street}
              onChange={(e) => handleInputChange('address.street', e.target.value)}
              className="col-span-2 bg-[#0f0f15] border-white/10 text-white"
            />
            <Input
              placeholder="Nº"
              value={form.address.number}
              onChange={(e) => handleInputChange('address.number', e.target.value)}
              className="bg-[#0f0f15] border-white/10 text-white"
            />
          </div>
          <Input
            placeholder="Complemento (opcional)"
            value={form.address.complement}
            onChange={(e) => handleInputChange('address.complement', e.target.value)}
            className="bg-[#0f0f15] border-white/10 text-white"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              placeholder="Bairro"
              value={form.address.neighborhood}
              onChange={(e) => handleInputChange('address.neighborhood', e.target.value)}
              className="bg-[#0f0f15] border-white/10 text-white"
            />
            <Input
              placeholder="Cidade"
              value={form.address.city}
              onChange={(e) => handleInputChange('address.city', e.target.value)}
              className="bg-[#0f0f15] border-white/10 text-white"
            />
          </div>
          <Input
            placeholder="Ponto de referência"
            value={form.address.reference}
            onChange={(e) => handleInputChange('address.reference', e.target.value)}
            className="bg-[#0f0f15] border-white/10 text-white"
          />
          
          {/* Delivery fee calculation status */}
          {calculatingDelivery && (
            <div className="flex items-center gap-2 text-white/60 text-sm p-3 bg-[#0f0f15] rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-[#ff6b00]" />
              Calculando taxa de entrega...
            </div>
          )}
          {deliveryFeeData && !calculatingDelivery && (
            <div className={`p-3 rounded-lg text-sm ${
              deliveryFeeData.available 
                ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`}>
              {deliveryFeeData.available ? (
                <>
                  <Check className="w-4 h-4 inline mr-2" />
                  {deliveryFeeData.message} - <strong>Taxa: R$ {deliveryFeeData.fee?.toFixed(2)}</strong>
                </>
              ) : (
                deliveryFeeData.message
              )}
            </div>
          )}
        </div>

        {/* Delivery Type - NOW AFTER ADDRESS */}
        <div className="card-adegga p-4 space-y-4">
          <h2 className="font-bold text-white">Tipo de Entrega</h2>
          <RadioGroup
            value={form.delivery_type}
            onValueChange={(v) => handleInputChange('delivery_type', v)}
            className="grid grid-cols-2 gap-3"
          >
            {store?.delivery_enabled && (
              <Label
                htmlFor="delivery"
                className={`flex flex-col p-4 rounded-lg border cursor-pointer transition-colors ${
                  form.delivery_type === 'delivery'
                    ? 'border-[#ff6b00] bg-[#ff6b00]/10'
                    : 'border-white/10 bg-[#0f0f15]'
                } ${!isDeliveryAvailable() && form.address.street ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem 
                    value="delivery" 
                    id="delivery" 
                    disabled={!isDeliveryAvailable() && form.address.street && deliveryFeeData}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Truck className="w-5 h-5 text-[#ff6b00]" />
                      <span className="text-white font-medium">Entrega</span>
                    </div>
                    <span className="text-sm text-[#ff6b00] font-bold">
                      {calculatingDelivery ? (
                        'Calculando...'
                      ) : deliveryFeeData?.available ? (
                        `R$ ${deliveryFeeData.fee?.toFixed(2)}`
                      ) : deliveryFeeData && !deliveryFeeData.available ? (
                        'Indisponível'
                      ) : store?.delivery_fee?.type === 'fixed' ? (
                        `R$ ${store?.delivery_fee?.fixed_value?.toFixed(2) || '0.00'}`
                      ) : (
                        'Digite seu endereço'
                      )}
                    </span>
                    {deliveryFeeData?.distance_km && deliveryFeeData.available && (
                      <span className="text-xs text-white/50 block">
                        {deliveryFeeData.distance_km.toFixed(1)} km de distância
                      </span>
                    )}
                  </div>
                </div>
              </Label>
            )}
            {store?.pickup_enabled && (
              <Label
                htmlFor="pickup"
                className={`flex flex-col p-4 rounded-lg border cursor-pointer transition-colors ${
                  form.delivery_type === 'pickup'
                    ? 'border-[#ff6b00] bg-[#ff6b00]/10'
                    : 'border-white/10 bg-[#0f0f15]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="pickup" id="pickup" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Store className="w-5 h-5 text-[#ff6b00]" />
                      <span className="text-white font-medium">Retirada</span>
                    </div>
                    <span className="text-sm text-green-400 font-bold">Grátis</span>
                    <span className="text-xs text-white/50 block">Retire na loja</span>
                  </div>
                </div>
              </Label>
            )}
          </RadioGroup>
        </div>

        {/* Payment */}
        <div className="card-adegga p-4 space-y-4">
          <h2 className="font-bold text-white">Forma de Pagamento</h2>
          <RadioGroup
            value={form.payment_method}
            onValueChange={(v) => handleInputChange('payment_method', v)}
            className="space-y-3"
          >
            <Label
              htmlFor="pix"
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                form.payment_method === 'pix'
                  ? 'border-[#ff6b00] bg-[#ff6b00]/10'
                  : 'border-white/10 bg-[#0f0f15]'
              }`}
            >
              <RadioGroupItem value="pix" id="pix" />
              <QrCode className="w-5 h-5 text-[#ff6b00]" />
              <span className="text-white">Pix</span>
            </Label>
            <Label
              htmlFor="cash"
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                form.payment_method === 'cash'
                  ? 'border-[#ff6b00] bg-[#ff6b00]/10'
                  : 'border-white/10 bg-[#0f0f15]'
              }`}
            >
              <RadioGroupItem value="cash" id="cash" />
              <Banknote className="w-5 h-5 text-[#ff6b00]" />
              <span className="text-white">Dinheiro</span>
            </Label>
          </RadioGroup>
          
          {form.payment_method === 'cash' && (
            <Input
              placeholder="Troco para quanto? (R$)"
              type="number"
              value={form.change_for}
              onChange={(e) => handleInputChange('change_for', e.target.value)}
              className="bg-[#0f0f15] border-white/10 text-white"
            />
          )}
          
          {form.payment_method === 'pix' && store?.pix_config?.key && (
            <div className="bg-[#0f0f15] p-4 rounded-lg">
              <p className="text-sm text-white/60 mb-2">Chave Pix:</p>
              <p className="text-[#ff6b00] font-mono break-all">{store.pix_config.key}</p>
              {store.pix_config.holder_name && (
                <p className="text-sm text-white/60 mt-2">{store.pix_config.holder_name}</p>
              )}
            </div>
          )}
        </div>

        {/* Coupon */}
        <div className="card-adegga p-4 space-y-4">
          <h2 className="font-bold text-white">Cupom de Desconto</h2>
          <div className="flex gap-2">
            <Input
              placeholder="Código do cupom"
              value={form.coupon_code}
              onChange={(e) => handleInputChange('coupon_code', e.target.value.toUpperCase())}
              className="bg-[#0f0f15] border-white/10 text-white uppercase"
            />
            <Button
              onClick={validateCoupon}
              disabled={couponLoading || !form.coupon_code}
              className="btn-secondary"
            >
              {couponLoading ? '...' : 'Aplicar'}
            </Button>
          </div>
          {coupon && (
            <div className="flex items-center gap-2 text-green-400">
              <Check className="w-4 h-4" />
              <span>Cupom aplicado: -{coupon.type === 'percentage' ? `${coupon.value}%` : `R$ ${coupon.value.toFixed(2)}`}</span>
            </div>
          )}
        </div>

        {/* Loyalty Points */}
        {loyalty?.enabled && loyalty.points > 0 && (
          <div className="card-adegga p-4 space-y-4 border border-[#ff6b00]/30">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-[#ff6b00]" />
              Programa de Fidelidade
            </h2>
            <div className="bg-[#0f0f15] p-4 rounded-lg">
              <div className="flex justify-between items-center mb-3">
                <span className="text-white/60">Seus pontos disponíveis</span>
                <span className="text-[#ff6b00] font-bold text-xl">{loyalty.points}</span>
              </div>
              
              {loyalty.points >= loyalty.min_points_redemption ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Resgatar pontos:</span>
                    <span className="text-white font-medium">{pointsToRedeem} pontos = R$ {getLoyaltyDiscount().toFixed(2)}</span>
                  </div>
                  <Slider
                    value={[pointsToRedeem]}
                    onValueChange={([value]) => setPointsToRedeem(value)}
                    max={loyalty.points}
                    min={0}
                    step={loyalty.min_points_redemption}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-white/40">
                    <span>0</span>
                    <span>{loyalty.points}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white/60">
                  Você precisa de pelo menos {loyalty.min_points_redemption} pontos para resgatar. 
                  Continue comprando para acumular mais!
                </p>
              )}
            </div>
            <p className="text-xs text-white/40 flex items-center gap-1">
              <Gift className="w-3 h-3" />
              Você ganha pontos a cada pedido concluído!
            </p>
          </div>
        )}

        {/* Notes */}
        <div className="card-adegga p-4 space-y-4">
          <h2 className="font-bold text-white">Observações</h2>
          <Textarea
            placeholder="Alguma observação para o pedido?"
            value={form.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className="bg-[#0f0f15] border-white/10 text-white min-h-[80px]"
          />
        </div>

        {/* Order Summary */}
        <div className="card-adegga p-4 space-y-3">
          <h2 className="font-bold text-white">Resumo do Pedido</h2>
          {cart.items.map((item, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-white/60">{item.quantity}x {item.product_name}</span>
              <span className="text-white">R$ {(item.unit_price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="border-t border-white/10 pt-3 space-y-2">
            <div className="flex justify-between">
              <span className="text-white/60">Subtotal</span>
              <span className="text-white">R$ {getTotal().toFixed(2)}</span>
            </div>
            {form.delivery_type === 'delivery' && (
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-white/60">Taxa de entrega</span>
                  {calculatingDelivery ? (
                    <span className="text-white/40">Calculando...</span>
                  ) : (
                    <span className="text-white">R$ {getDeliveryFee().toFixed(2)}</span>
                  )}
                </div>
                {deliveryFeeData?.distance_km && (
                  <p className="text-xs text-white/40 text-right">
                    {deliveryFeeData.distance_km}km de distância
                  </p>
                )}
                {deliveryFeeData?.available === false && (
                  <p className="text-xs text-red-400 text-right">
                    {deliveryFeeData.message}
                  </p>
                )}
              </div>
            )}
            {getDiscount() > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Desconto (cupom)</span>
                <span>-R$ {getDiscount().toFixed(2)}</span>
              </div>
            )}
            {getLoyaltyDiscount() > 0 && (
              <div className="flex justify-between text-[#ff6b00]">
                <span>Desconto (pontos)</span>
                <span>-R$ {getLoyaltyDiscount().toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold">
              <span className="text-white">Total</span>
              <span className="text-[#ff6b00]">R$ {getFinalTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0a0a0f] border-t border-white/5 p-4 z-40">
        <div className="max-w-4xl mx-auto">
          <Button
            className="w-full btn-primary h-14 text-lg flex items-center justify-center gap-2"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-order-btn"
          >
            {submitting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              <>
                <MessageCircle className="w-5 h-5" />
                Enviar Pedido via WhatsApp
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
